import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, IconButton, Stack, Tooltip } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import SubmitButton from "shared/ui/SubmitButton";
import { useTranslation } from "shared/hooks/useTranslation";

export interface FormActionsProps {
    submitLabel: string;
    /** Use with submitButtonType=\"submit\" inside a `<form>`; otherwise pass onSubmit for click handlers. */
    onSubmit?: () => void;
    submitButtonType?: "button" | "submit";
    onCancel: () => void;
    submitDisabled?: boolean;
    submitLoading?: boolean;
    submitColor?: ButtonProps["color"];
    cancelLabel?: string;
    cancelDisabled?: boolean;
    /** Optional destructive action (e.g. delete); shown after cancel as icon button with tooltip. */
    destructiveAction?: { label: string; onClick: () => void; disabled?: boolean };
}

/**
 * Primary submit + outlined cancel (+ optional destructive) in one row. Matches app form CTA rhythm.
 */
export default function FormActions({
    submitLabel,
    onSubmit,
    submitButtonType = "button",
    onCancel,
    submitDisabled,
    submitLoading,
    submitColor = "primary",
    cancelLabel,
    cancelDisabled,
    destructiveAction,
}: FormActionsProps) {
    const { t } = useTranslation();

    const effectiveCancelLabel = cancelLabel ?? t("common.cancel");

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2, alignItems: "stretch" }}>
            <SubmitButton
                type={submitButtonType}
                variant="contained"
                color={submitColor}
                fullWidth
                sx={{ flex: { sm: 1 } }}
                disabled={submitDisabled}
                loading={submitLoading}
                onClick={submitButtonType === "button" ? onSubmit : undefined}
            >
                {submitLabel}
            </SubmitButton>
            <Stack direction="row" spacing={1} sx={{ flex: { sm: 1 }, minWidth: 0, alignItems: "center" }}>
                <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    onClick={onCancel}
                    disabled={cancelDisabled || submitLoading}
                >
                    {effectiveCancelLabel}
                </Button>
                {destructiveAction ? (
                    <Tooltip title={destructiveAction.label}>
                        <span>
                            <IconButton
                                type="button"
                                color="error"
                                aria-label={destructiveAction.label}
                                onClick={destructiveAction.onClick}
                                disabled={
                                    destructiveAction.disabled ?? submitLoading ?? cancelDisabled
                                }
                                sx={{ flexShrink: 0, border: (theme) => `1px solid ${theme.palette.divider}` }}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
            </Stack>
        </Stack>
    );
}
